import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { BoxTshirtEdit } from './components/BoxTshirtEdit';
import { CoverImageGoldDate } from './components/CoverImage';
import InfoContent from './components/InfoContent';
import { MoreInfoButton } from './components/MoreInfoButton';
import RankingTable from './components/RankingTable';
import { SidebarContentView } from './components/SidebarContentView';

import { Button, Loader, Modal } from '@/components';
import { Alert } from '@/components/Alert';
import { Layout, MainHome } from '@/components/GenericsTournaments';
// import { useCbuModal } from '@/hooks/useCbuModal';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import { requestViewGoldDate } from '@/services/goldDate';
import { EnumDevice } from '@/types';
import { SelectedScreen } from '@/types/editProfile';
import {
  ResponseInfoFeoroUsuario,
  ResponseVerFechaOro,
} from '@/types/goldDate';
import { EnumCoverGoldDate } from '@/types/minileagues';
import { EnumContextGDT } from '@/types/team';
import { clearLocationState, formatNumberToLocale } from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

export const GoldDateRanking = () => {
  const { tourneyData, updateTourney } = useTourney();
  const [stateData, setStateData] = useState<ResponseVerFechaOro | null>(null);
  // const { ModalCBU, setModalCBU } = useCbuModal();
  const { isDesktop, isMobile } = useIsDesktop();
  const navigate = useNavigate();

  const location = useLocation();

  const dataFO = useMemo(() => {
    return tourneyData.tourney as ResponseInfoFeoroUsuario;
  }, [tourneyData]);

  const { isLoading } = useQuery(
    'initRankingTda',
    async () => await requestViewGoldDate(tourneyData.id || 0),
    {
      onSuccess: async (response: ResponseVerFechaOro) => {
        if (response.estado) {
          setStateData(response);
          // setModalCBU({
          //   title: 'Fecha de Oro',
          //   visibility: Boolean(response.muestroPopUpDatosBancarios),
          // });
        } else {
          console.error('Error obteniendo datos de inicio de Fecha de Oro.');
        }
      },
      onError: (error) => {
        console.error(
          'Error obteniendo datos de inicio de Fecha de Oro:',
          error
        );
        throw new Error('Invalid response state');
      },
      enabled: Boolean(location?.state),
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    }
  );

  useEffect(() => {
    return () => {
      // seteo state en null para realizar peticion si es nesario... soluciono problemas de back en el sitio
      clearLocationState();
    };
  }, []);

  if (!location.state) {
    // redirecciono al listado si no tengo ID de torneo
    navigate(ROUTES.goldDate.list, { state: null });
  }

  const { userInitialData } = useAuth();

  const titleCover = useMemo(() => {
    if (stateData?.algunaFechaDelTAPublicada) {
      if (
        stateData.posicion === '1' ||
        stateData.top_fecha[0].idUsuarioDT === userInitialData?.idUsuarioDT
      ) {
        return (
          <p className="lg:leading-8 lg:text-center text-xl lg:text-3xl font-[100] lg:font-[100]">
            ¡FeliciTACI0NES!
            <br />
            sos el campeón de la fecha de oro.
          </p>
        );
      } else {
        return (
          <p className="lg:leading-8 lg:text-center text-xl lg:text-3xl font-[100] lg:font-[100]">
            ¡FeliciTACI0NES a {stateData.nombreGanadorFEORO},
            <br />
            el campeón de la fecha de oro!
          </p>
        );
      }
    }

    return (
      <p className="lg:max-w-none max-w-[240px] font-[100] lg:font-[100]">
        ¡El ganador de la fecha de oro se lleva $
        {formatNumberToLocale(dataFO?.premio ?? 0)}!
      </p>
    );
  }, [dataFO?.premio, stateData, userInitialData?.idUsuarioDT]);

  return (
    <>
      <Loader isVisible={isLoading} timeout={isLoading} />
      <Layout
        adsSections={SECTION_NAME_FOR_ADS.fechadeoro}
        backButton={isDesktop && true}
        backButtonAction={() => navigate(ROUTES.goldDate.list)}
        className="p-4"
        coverTop={() => (
          <>
            {/*  ALERT CBU */}
            {isMobile && !stateData?.tieneDB && (
              <Alert
                className="w-full mt-2"
                message={
                  <>
                    Cargá los datos de tu CBU por si resultás ser el ganador.{' '}
                    <Button
                      className="no-underline text-white text-xs font-semibold"
                      type="button"
                      variant="text"
                      onClick={() => {
                        navigate(ROUTES.editAccount.myCBU, {
                          state: {
                            selectedScreen: 'MyCBU',
                          } as SelectedScreen,
                        });
                      }}
                    >
                      Completar
                    </Button>
                  </>
                }
                variant={EnumDevice.Mobile}
              />
            )}

            {/* BOX CAMISETA */}
            {isMobile && <BoxTshirtEdit position={stateData?.posicion} />}

            {/* BG COVER */}
            <CoverImageGoldDate
              caption={titleCover}
              context={
                stateData?.algunaFechaDelTAPublicada
                  ? EnumCoverGoldDate.Winner
                  : EnumCoverGoldDate.Default
              }
            />
          </>
        )}
        main={() => (
          <MainHome
            context={EnumContextGDT.GoldDate}
            subtitle={isDesktop ? '20 PARTICIPANTES' : undefined}
            title="Fecha de Oro"
          >
            <div className="lg:pr-5">
              {stateData && <RankingTable {...stateData} />}
            </div>

            {isMobile && (
              <div className="text-center mt-4 flex flex-col space-y-3">
                <Button
                  className="text-gdt-blue font-bold"
                  type="button"
                  variant="text"
                  onClick={() => navigate(ROUTES.myTeam11)}
                >
                  Ver mi equipo
                </Button>
                <MoreInfoButton />
              </div>
            )}
          </MainHome>
        )}
        mobile={{
          leftContent: {
            backAction: () => {
              navigate(ROUTES.goldDate.list);
            },
          },
          centerContent: {
            title: (
              <>
                <h1>
                  {'Fecha de Oro ' +
                    stateData?.nombre.split(' ')[
                      stateData?.nombre.split(' ').length - 1
                    ]}
                </h1>
                <p className="uppercase text-2xs font-light">
                  {stateData?.nombreFechaRanking}
                </p>
              </>
            ),
          },
        }}
        sidebar={() => {
          return isDesktop && <SidebarContentView tourney={stateData} />;
        }}
      />

      {/* MODAL INFO */}
      <Modal
        className="w-[600px] text-center px-[38px]"
        controlledOnClose={() => {
          updateTourney({ showModalInfo: false });
        }}
        disableBodyScroll={true}
        isControlledOpen={Boolean(tourneyData.showModalInfo) && isDesktop}
        render={() => <InfoContent />}
        size="sm"
        variant="controlled"
      />

      {/* <ModalCBU /> */}
    </>
  );
};
